import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchCountries } from '@eliadmin/core/src/api/general/general-api';
import { JobportalAddExperienceField as Field } from '@eliadmin/core/src/components/jobportal/jobportal-create-curriculum/jobportal-add-experience/jobportal-add-experience-form-fields';
import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import {
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  useResponsiveDialogStore,
} from '@eliadmin/core/src/components/ui/responsive-dialog';
import { Textarea } from '@eliadmin/core/src/components/ui/textarea';
import {
  useFormValidationMessages,
  type FormValidationMessages,
} from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { CreateCurriculumSchema } from '@eliadmin/core/src/pages/(jobportal)/create-curriculum/page';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { monthsValues } from '@eliadmin/core/src/utils/date-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { Separator } from '@radix-ui/react-separator';
import { useQuery } from '@tanstack/react-query';
import { useForm, useFormContext } from 'react-hook-form';
import { z } from 'zod';

export const addExperienceSchema = (VM: FormValidationMessages) =>
  z
    .object({
      id: z.number(),
      name: z.string().min(1, VM.REQUIRED_FIELD_VALIDATION_MESSAGE()),
      employer: z.string().min(1, VM.REQUIRED_FIELD_VALIDATION_MESSAGE()),
      city: z.string().min(1, VM.REQUIRED_FIELD_VALIDATION_MESSAGE()),
      //   country: z.string().min(1, VM.REQUIRED_FIELD_VALIDATION_MESSAGE()),
      still_working: z.boolean(),
      startMonth: z.string(),
      startYear: z.string().min(1, VM.REQUIRED_FIELD_VALIDATION_MESSAGE()),
      endMonth: z.string(),
      endYear: z.string(),
      description: z.string(),
    })
    .superRefine((data, ctx) => {
      const checkMonth = (fieldName: any, required = true, condition = true) => {
        if (!required && data[fieldName as keyof typeof data] === '') {
          return;
        }

        const monthInteger = parseInt(data[fieldName as keyof typeof data] as string, 10);

        if (!monthInteger && monthInteger !== 0) {
          ctx.addIssue({
            path: [fieldName],
            message: VM.INVALID_NUMBER_VALIDATION_MESSAGE(),
            code: z.ZodIssueCode.custom,
          });
        }

        if (
          condition &&
          (!data[fieldName as keyof typeof data] || monthInteger < 0 || monthInteger > 11)
        ) {
          ctx.addIssue({
            path: [fieldName],
            message: VM.NOT_IN_RANGE_VALIDATION_MESSAGE(1, 12),
            code: z.ZodIssueCode.custom,
          });
        }
      };

      const checkYear = (fieldName: any, condition = true) => {
        const yearString = data[fieldName as keyof typeof data] as string;

        if (!yearString) {
          ctx.addIssue({
            path: [fieldName],
            message: VM.REQUIRED_FIELD_VALIDATION_MESSAGE(),
            code: z.ZodIssueCode.custom,
          });
        }

        const yearInteger = parseInt(yearString, 10);

        if (!yearInteger) {
          ctx.addIssue({
            path: [fieldName],
            message: VM.INVALID_NUMBER_VALIDATION_MESSAGE(),
            code: z.ZodIssueCode.custom,
          });
        }

        const currentYear = new Date().getFullYear();

        if (
          condition &&
          (!data[fieldName as keyof typeof data] || yearInteger < 1900 || yearInteger > currentYear)
        ) {
          ctx.addIssue({
            path: [fieldName],
            message: VM.NOT_IN_RANGE_VALIDATION_MESSAGE(1900, currentYear),
            code: z.ZodIssueCode.custom,
          });
        }
      };

      checkYear('startYear');
      checkMonth('startMonth', false);
      if (!data.still_working) {
        checkYear('endYear');
        checkMonth('endMonth', false);
      }
    });

export type AddExperienceSchemaType = z.infer<ReturnType<typeof addExperienceSchema>>;

type JobportalAddExperienceDialogProps = {
  onSubmit?: (data: AddExperienceSchemaType) => void;
  onEdit?: (data: AddExperienceSchemaType) => void;
  dataToEdit?: AddExperienceSchemaType;
  newId?: number;
};

export const JobportalAddExperienceDialog = ({
  onSubmit,
  onEdit,
  dataToEdit,
  newId,
}: JobportalAddExperienceDialogProps) => {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);

  const close = useResponsiveDialogStore(s => s.close);

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<z.infer<ReturnType<typeof addExperienceSchema>>>({
    resolver: zodResolver(addExperienceSchema(formValidationMessages)),
    defaultValues: onEdit
      ? {
          id: dataToEdit?.id,
          name: dataToEdit?.name || '',
          employer: dataToEdit?.employer || '',
          city: dataToEdit?.city || '',
          //   country: dataToEdit?.country || '',
          still_working: dataToEdit?.endMonth || dataToEdit?.endYear ? false : true,
          startMonth: dataToEdit?.startMonth || '',
          startYear: dataToEdit?.startYear || '',
          endMonth: dataToEdit?.endMonth || '',
          endYear: dataToEdit?.endYear || '',
          description: dataToEdit?.description || '',
        }
      : {
          id: newId || 0,
          name: '',
          employer: '',
          city: '',
          //   country: '',
          still_working: false,
          startMonth: '',
          startYear: '',
          endMonth: '',
          endYear: '',
          description: '',
        },
  });

  const stillWorking = form.watch('still_working');

  const { data: countries = { data: [] } } = useQuery({
    queryKey: [CacheKey.COUNTRIES, locale],
    queryFn: () => fetchCountries({ locale }),
    refetchOnWindowFocus: false,
  });

  const handleSubmit = form.handleSubmit(
    async data => {
      close(JobportalAddExperienceDialog.id);
      if (onSubmit) {
        onSubmit(data);
      } else if (onEdit) {
        onEdit(data);
      }
    },
    errors => {
      // eslint-disable-next-line no-console
      if (process.env.NODE_ENV === 'development') console.error('Form errors:', errors);
    },
  );

  return (
    <>
      <ResponsiveDialogContent
        dialogProps={{
          className:
            'max-w-screen max-h-[94vh] w-[680px] overflow-y-auto sm:max-w-[600px] md:max-w-[680px] lg:max-w-[680px] ea-jobportal-add-experience-dialog__content ea-jobportal-dialog__content',
        }}
        drawerProps={{
          className:
            'pb-6 max-h-screen ea-jobportal-add-experience-dialog__content ea-jobportal-dialog__content',
        }}
      >
        <form
          onSubmit={handleSubmit}
          className={cn(
            'flex flex-col gap-6 overflow-auto px-4',
            'ea-jobportal-add-experience-dialog__form ea-jobportal-dialog__form',
          )}
        >
          <Form {...form}>
            <ResponsiveDialogHeader
              className={cn(
                'ea-jobportal-add-experience-dialog__header ea-add-experience-dialog__header ea-jobportal-dialog__header',
              )}
            >
              <ResponsiveDialogTitle
                className={cn(
                  'font-semibold',
                  'ea-jobportal-add-experience-dialog__header-title ea-jobportal-dialog__header-title',
                )}
              >
                {t('text955')}
              </ResponsiveDialogTitle>
            </ResponsiveDialogHeader>
            <div
              className={cn(
                'grid grid-cols-12 gap-x-4 gap-y-3',
                'ea-jobportal-dialog__form-content ea-jobportal-add-experience-dialog__form-content',
              )}
            >
              <Field control={form.control} name="name" label={t('text956')} />
              <Field control={form.control} name="employer" label={t('text957')} />
              <Field control={form.control} name="city" label={t('text958')} />
              {/* <Field
                control={form.control}
                name="country"
                type={'select'}
                label={t('text959')}
                options={countries.data.map(c => ({ label: c.name, value: c.id }))}
              /> */}
              <Separator
                className={cn(
                  'col-span-12 my-2 h-[1px] bg-border',
                  'ea-jobportal-dialog__separator ea-jobportal-add-experience-dialog__separator',
                )}
              />
              <h3
                className={cn(
                  'col-span-12 text-lg font-semibold',
                  'ea-jobportal-dialog__section-header ea-jobportal-add-experience-dialog__section-header',
                )}
              >
                {t('text960')}
              </h3>
              <div className="col-span-12">
                <Field
                  control={form.control}
                  name="still_working"
                  type={'switch'}
                  label={t('text961')}
                />
              </div>
              <Field
                control={form.control}
                name="startMonth"
                type="select"
                options={monthsValues(t)}
                label={t('text962')}
              />
              <Field control={form.control} name="startYear" label={t('text963')} />
              {!stillWorking && (
                <>
                  <Field
                    control={form.control}
                    name="endMonth"
                    type="select"
                    options={monthsValues(t)}
                    label={t('text964')}
                  />
                  <Field control={form.control} name="endYear" label={t('text965')} />
                </>
              )}
              <Separator
                className={cn(
                  'col-span-12 my-2 h-[1px] bg-border',
                  'ea-jobportal-dialog__separator ea-jobportal-add-experience-dialog__separator',
                )}
              />
              <h3
                className={cn(
                  'col-span-12 text-lg font-semibold',
                  'ea-jobportal-dialog__section-header ea-jobportal-add-experience-dialog__section-header',
                )}
              >
                {t('text966')}
              </h3>
              <p
                className={cn(
                  'col-span-12 text-muted-foreground',
                  'ea-jobportal-dialog__section-description ea-jobportal-add-experience-dialog__section-description',
                )}
              >
                {t('text967')}
              </p>
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'col-span-12 mt-1',
                      'ea-jobportal-add-experience-dialog__value-field',
                    )}
                    required
                  >
                    <FormControl
                      className={cn('', 'ea-jobportal-add-experience-dialog__value-textarea')}
                    >
                      <Textarea {...field} rows={3} />
                    </FormControl>
                    <FormMessage
                      className={cn('', 'ea-jobportal-add-experience-dialog__value-message')}
                    />
                  </FormItem>
                )}
              />
              <Separator
                className={cn(
                  'col-span-12 my-2 h-[1px] bg-border',
                  'ea-jobportal-dialog__separator ea-jobportal-add-experience-dialog__separator',
                )}
              />
              <div
                className={cn(
                  'col-span-12 flex items-center justify-end gap-2',
                  'ea-jobportal-dialog__buttons-wrapper ea-jobportal-add-experience-dialog__buttons-wrapper',
                )}
              >
                <Button
                  variant={'default-alt'}
                  className={cn(
                    '',
                    'ea-jobportal-dialog__cancel-button ea-jobportal-add-experience-dialog__cancel-button',
                  )}
                >
                  {t('text377')}
                </Button>
                <Button
                  className={cn(
                    '',
                    'ea-jobportal-dialog__submit-button ea-jobportal-add-experience-dialog__submit-button',
                  )}
                >
                  {t('text201')}
                </Button>
              </div>
            </div>
          </Form>
        </form>
      </ResponsiveDialogContent>
    </>
  );
};

JobportalAddExperienceDialog.id = 'JOBPORTAL_ADD_EXPERIENCE_DIALOG';
